import { INITIAL_PAGE, NEXT_PAGE, PREV_PAGE } from '../constants/pagination';

/**
 * @type {import('./index.d').getWindowLocation}
 */
export const getWindowLocation = () => {
  // Escape window.location.href
  const href = window.location.href;
  const escapedHref = href.replace(/%3C/g, '&lt;').replace(/%3E/g, '&gt;');

  // Rebuild window.location.href
  const rebuildHrefArr = [];
  for (let i = 0; i < escapedHref.length; i++) {
    rebuildHrefArr.push(escapedHref.charAt(i));
  }
  const rebuildHref = rebuildHrefArr.join('').split('&lt;').join('%3C').split('&gt;').join('%3E');

  // Rebuild window.location.search
  let rebuildSearch = '';
  const hrefWithoutHash = rebuildHref.replace(/#.*$/, '');
  if (hrefWithoutHash.split('?').length > 1) {
    rebuildSearch = hrefWithoutHash.split('?')[1];
    if (rebuildSearch.length > 0) {
      rebuildSearch = '?' + rebuildSearch;
    }
  }

  return {
    pathname: window.location.pathname,
    href: rebuildHref,
    search: rebuildSearch,
  };
};

/**
 * @type {import('./index.d').setWindowLocation}
 */
export const setWindowLocation = (url) => {
  window.location.href = url;
};

/**
 * @type {import('./index.d').isMobile}
 */
export const isMobile = (breakpointMobile = 991) => {
  return window.innerWidth <= breakpointMobile;
};

/**
 * @type {import('./index.d').getSessionStorage}
 */
export const getSessionStorage = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    if (!value) return null;
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

/**
 * @type {import('./index.d').setSessionStorage}
 */
export const setSessionStorage = (key, value) => {
  try {
    if (!value) {
      sessionStorage.removeItem(key);
      return;
    }

    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    sessionStorage.setItem(key, '');
  }
};

/**
 * @type {import('./index.d').getLocalStorage}
 */
export const getLocalStorage = (key) => {
  try {
    const value = localStorage.getItem(key);
    if (value) return JSON.parse(value);

    return null;
  } catch (error) {
    return null;
  }
};

/**
 * @type {import('./index.d').setLocalStorage}
 */
export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.log('Error setLocalStorage', error);
  }
};

/**
 * @type {import('./index.d').removeLocalStorage}
 */
export const removeLocalStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.log('Error setLocalStorage', error);
  }
};

/**
 * @type {import('./index.d').handleClickOutside}
 */
export const handleClickOutside = (rootElm, callback) => {
  const onClick = (event) => {
    if (!rootElm.contains(event.target)) {
      callback(event.target);
      document.removeEventListener('click', onClick);
    }
  };

  document.addEventListener('click', onClick);

  // Optional: Cleanup function to remove event listener
  return () => document.removeEventListener('click', onClick);
};

/**
 * @type {import('./index.d').setPaginationSession}
 */
export const setPaginationSession = (page) => {
  // Set pages for pagination
  page = Number(page);

  if (!page || Number.isNaN(page)) page = 1;

  setSessionStorage(INITIAL_PAGE, page);
  setSessionStorage(PREV_PAGE, page);
  setSessionStorage(NEXT_PAGE, page);
};

/**
 * @type {import('./index.d').inViewPortHandler}
 */
export function inViewPortHandler(elements, callback) {
  const observer = new IntersectionObserver(function intersectionObserverCallback(
    entries,
    observer
  ) {
    entries.forEach(function intersectionEntryCallback(entry) {
      if (entry.isIntersecting) {
        callback(entry.target);
        observer.unobserve(entry.target);
      }
    });
  });
  elements.forEach(function (element) {
    observer.observe(element);
  });
}

/**
 * @type {import('./index.d').isMobileWidth}
 */
export function isMobileWidth() {
  return window.innerWidth < 576;
}

/**
 * @type {import('./index.d').isTabletPortraitMaxWidth}
 */
export function isTabletPortraitMaxWidth() {
  return window.innerWidth < 991;
}

/**
 * @type {import('./index.d').detectDeviceByWidth}
 */
export function detectDeviceByWidth() {
  let result = '';
  if (isMobileWidth()) {
    result += 'mobile|';
  } else {
    result = result.replace('mobile|', '');
  }

  if (isTabletPortraitMaxWidth()) {
    result += 'tablet_portrait_max';
  } else {
    result = result.replace('tablet_portrait_max', '');
  }

  return result;
}

/**
 * @type {import('./index.d').requestIdleCallback}
 */
export const requestIdleCallback =
  // eslint-disable-next-line compat/compat
  window.requestIdleCallback ||
  function (cb) {
    const start = Date.now();
    return setTimeout(function () {
      cb({
        didTimeout: false,
        timeRemaining: function () {
          return Math.max(0, 50 - (Date.now() - start));
        },
      });
    }, 1);
  };

// eslint-disable-next-line compat/compat
window.requestIdleCallback = requestIdleCallback;

/**
 * @type {import('./index.d').cancelIdleCallback}
 */
export const cancelIdleCallback =
  window.cancelIdleCallback ||
  function (id) {
    clearTimeout(id);
  };
